/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { RefObject } from 'react';
import { useState } from 'react';

import { ConfirmModal } from '@coursera-web/cds-labs';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { Button, Card, Grid, InlineNotification, Typography2, breakpoints, useToast } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { delink } from 'bundles/third-party-auth/actions/AccountLinkActions';
import {
  getSsoErrorMessage,
  getSsoSuccessMessage,
} from 'bundles/third-party-auth/components/AccountLink/utils/messages';
import AccountLinkStore from 'bundles/third-party-auth/stores/AccountLinkStore';

import _t from 'i18n!nls/third-party-auth';

const styles = {
  row: css`
    gap: var(--cds-spacing-200);
    align-items: flex-start;
    flex-wrap: wrap;

    ${breakpoints.down('xs')} {
      gap: var(--cds-spacing-100);
    }
  `,
  textColumn: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  ctaColumn: css`
    padding-top: var(--cds-spacing-50);
  `,
  card: css`
    &.cds-Card-base .cds-Card-innerCard {
      padding: var(--cds-spacing-200);
    }
  `,
  liveRegion: css`
    min-height: 0;

    &:not(:empty) {
      margin-top: var(--cds-spacing-200);
    }
  `,
};

type Props = {
  buttonRef?: RefObject<HTMLButtonElement>;
};

const AccountLinkSSO = ({ buttonRef }: Props) => {
  const { linked, loading, errorCode } = useFluxibleStore(AccountLinkStore, (store) => ({
    linked: store.linked || {},
    loading: store.loading,
    errorCode: store.errorCode,
  }));
  const executeAction = useFluxibleAction();
  const { show: showToast } = useToast();
  const track = useTracker();

  const [showConfirm, setShowConfirm] = useState(false);

  if (!linked.saml && !linked.jwt) {
    return null;
  }

  const handleDelink = () => {
    track('click_button', {
      button: { name: 'account_setting_delink_sso_btn' },
    });
    executeAction(delink, {
      accountType: linked.saml ? 'saml' : 'jwt',
      onSuccess: () => {
        const message = getSsoSuccessMessage('delink');
        if (message) {
          showToast({
            message,
            role: 'status',
            durationMs: 4000,
          });
        }
      },
    });
  };

  const errorMessage = getSsoErrorMessage(errorCode);

  return (
    <Card css={styles.card} interactive={false}>
      <div>
        <Grid container css={styles.row}>
          <Grid item xs css={styles.textColumn}>
            <Typography2 variant="subtitleLarge" component="h3">
              {_t('Organization account')}
            </Typography2>
            <Typography2 variant="bodySecondary" component="p" color="supportText">
              {_t('Your Coursera account is linked to your organization account.')}
            </Typography2>
          </Grid>
          <Grid item xs={12} sm="auto" css={styles.ctaColumn}>
            <Button
              ref={buttonRef}
              variant="secondary"
              size="small"
              loading={loading}
              onClick={() => setShowConfirm(true)}
            >
              {_t('Unlink account')}
            </Button>
          </Grid>
        </Grid>
        <div role="alert" aria-live="assertive" aria-atomic="true" css={styles.liveRegion}>
          {errorMessage && <InlineNotification severity="error">{errorMessage}</InlineNotification>}
        </div>
      </div>
      <ConfirmModal
        open={showConfirm}
        returnFocusRef={buttonRef}
        headerLabel={_t('Are you sure you want to unlink your organization account?')}
        acceptLabel={_t('Unlink')}
        onAccept={() => {
          setShowConfirm(false);
          handleDelink();
        }}
        onCancel={() => setShowConfirm(false)}
      />
    </Card>
  );
};

export default AccountLinkSSO;
